import React, { useCallback, useEffect, useRef, useState } from "react";
import { Col, DatePicker, Empty, notification, Row, Select, Spin } from "antd";
import { Formik } from "formik";
import { useSelector } from "react-redux";
import CanvasJSReact from "../../../assets/canvasjs.react";
import ButtonStandard from "../../../components/utils/button";
import { getPicketIndexDropDown } from "../../../services/picketService";
import { momentTimeZoneZeroHour } from "../../../utils/dateUtils";
import { getDashboardDataService } from "../../../services/picketSupplementSupplyService";
import { DashboardContainer } from "../styles";
import { getLotIndexDropDown } from "../../../services/lotService";

// import { Container } from './styles';

const LIST_PERIODS = [
  "lastSevenDays",
  "lastThirdyDays",
  "currentMonth",
  "lastMonth",
  "currentYear",
  "lastYear",
  "currentHarvest",
  "lastHarvest",
  "customDateRange",
];

const { CanvasJSChart } = CanvasJSReact;

const INITIAL_PARAMETERS_VALUES = {
  lotIds: [],
  picketIds: [],
  startDate: null,
  endDate: null,
  periodType: "lastSevenDays",
};

function PicketSupplementSupplyTableDashboard() {
  let chartRef = useRef(null);

  const [pickets, setPickets] = useState([]);
  const [lots, setLots] = useState([]);

  const [dashboardType, setDashboardType] = useState("line");

  const [loading, setLoading] = useState(false);

  const [dashboardData, setDashboardData] = useState([]);

  const [MSConsumptionData, setMSConsumptionData] = useState([]);
  const [NDTConsumptionData, setNDTConsumptionData] = useState([]);
  const [PBConsumptionData, setPBConsumptionData] = useState([]);
  const [IMSData, setIMSData] = useState([]);

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const toggleDataSeries = useCallback((e) => {
    if (typeof e.dataSeries.visible === "undefined" || e.dataSeries.visible) {
      e.dataSeries.visible = false;
    } else {
      e.dataSeries.visible = true;
    }
    e.chart.render();
  }, []);

  const generateChart = useCallback(() => {
    // Chart Expense Values
    const msData = dashboardData.map((data) => {
      return {
        x: momentTimeZoneZeroHour(data.date).toDate(),
        label: momentTimeZoneZeroHour(data.date).format("DD/MM/YYYY"),
        y: data.consumeMs,
      };
    });
    const ndtData = dashboardData.map((data) => {
      return {
        x: momentTimeZoneZeroHour(data.date).toDate(),
        label: momentTimeZoneZeroHour(data.date).format("DD/MM/YYYY"),
        y: data.consumeNdt,
      };
    });
    const pbData = dashboardData.map((data) => {
      return {
        x: momentTimeZoneZeroHour(data.date).toDate(),
        label: momentTimeZoneZeroHour(data.date).format("DD/MM/YYYY"),
        y: data.consumePb,
      };
    });
    const imsData = dashboardData.map((data) => {
      return {
        x: momentTimeZoneZeroHour(data.date).toDate(),
        label: momentTimeZoneZeroHour(data.date).format("DD/MM/YYYY"),
        y: data.imsPv,
      };
    });
    setMSConsumptionData(msData);
    setNDTConsumptionData(ndtData);
    setPBConsumptionData(pbData);
    setIMSData(imsData);
  }, [dashboardData]);

  const getLots = useCallback(async () => {
    const {
      data: { results },
    } = await getLotIndexDropDown({
      groupId,
      farmId,
    });
    setLots(
      results.filter(
        (lot) =>
          lot.status === "Active" ||
          lot.status === "A" ||
          lot.status === "Pending" ||
          lot.status === "P",
      ),
    );
  }, [groupId, farmId]);

  const getPickets = useCallback(async () => {
    const {
      data: { results: pickets },
    } = await getPicketIndexDropDown({
      groupId,
      farmId,
    });
    setPickets(pickets);
  }, [groupId, farmId]);

  const getDashboardData = useCallback(
    async (parameters = INITIAL_PARAMETERS_VALUES) => {
      // Fetch the dashboard data from the API
      setLoading(true);
      try {
        const {
          data: { results },
        } = await getDashboardDataService({
          groupId,
          farmId,
          parameters: {
            ...parameters,
            startDate: parameters.startDate
              ? parameters.startDate.format("YYYY-MM-DD")
              : null,
            endDate: parameters.endDate
              ? parameters.endDate.format("YYYY-MM-DD")
              : null,
          },
        });
        setDashboardData(results.values);
      } catch (error) {
        notification.error({
          message: "Erro ao buscar dados do dashboard",
        });
      } finally {
        setLoading(false);
      }
    },
    [farmId, groupId],
  );

  const handleFilterDashboard = useCallback(
    (values) => {
      // Handle form submission logic here
      getDashboardData(values);
    },
    [getDashboardData],
  );

  const optionsDashboardDataColumn = [
    {
      type: "column",
      xValueFormatString: "DD/MM/YY",
      name: "CMS KG",
      showInLegend: true,
      yValueFormatString: "#,##0.00",
      color: "blue",
      dataPoints: MSConsumptionData,
    },
    {
      type: "column",
      xValueFormatString: "DD/MM/YY",
      name: "CMS NDT KG",
      showInLegend: true,
      yValueFormatString: "#,##0.00",
      color: "#33c162",
      dataPoints: NDTConsumptionData,
    },
    {
      type: "column",
      xValueFormatString: "DD/MM/YY",
      name: "CMS PB KG",
      showInLegend: true,
      yValueFormatString: "#,##0.00",
      color: "#f2a900",
      dataPoints: PBConsumptionData,
    },
    {
      type: "column",
      xValueFormatString: "DD/MM/YY",
      name: "CMS %PV",
      showInLegend: true,
      color: "#f44336",
      yValueFormatString: "#,##0.00",
      axisYType: "secondary",
      dataPoints: IMSData,
    },
  ];

  const optionsDashboardDataLine = [
    {
      type: "line",
      xValueFormatString: "DD/MM/YY",
      name: "CMS KG",
      showInLegend: true,
      yValueFormatString: "#,##0.00",
      color: "blue",
      dataPoints: MSConsumptionData,
    },
    {
      type: "line",
      xValueFormatString: "DD/MM/YY",
      name: "CMS NDT KG",
      showInLegend: true,
      yValueFormatString: "#,##0.00",
      color: "#33c162",
      dataPoints: NDTConsumptionData,
    },
    {
      type: "line",
      xValueFormatString: "DD/MM/YY",
      name: "CMS PB KG",
      showInLegend: true,
      yValueFormatString: "#,##0.00",
      color: "#f2a900",
      dataPoints: PBConsumptionData,
    },
    {
      type: "line",
      xValueFormatString: "DD/MM/YY",
      name: "CMS %PV",
      showInLegend: true,
      color: "#f44336",
      yValueFormatString: "#,##0.00",
      axisYType: "secondary",
      dataPoints: IMSData,
    },
  ];

  const optionsDashboard = {
    culture: "br",
    animationEnabled: true,
    exportEnabled: false,
    zoomEnabled: true,
    axisX: {
      valueFormatString: "DD/MM/YY",
      crosshair: {
        enabled: true,
        snapToDataPoint: true,
      },
    },
    axisY: {
      includeZero: true,
      valueFormatString: "#,##0.00",
      suffix: "kgMS",
      gridDashType: "dot",
    },
    axisY2: {
      includeZero: true,
      valueFormatString: "#,##0.00",
      suffix: "% MS(%PV)",
      gridDashType: "dot",
    },
    toolTip: {
      shared: true,
      reversed: false,
    },
    legend: {
      verticalAlign: "center",
      horizontalAlign: "right",
      reversed: true,
      cursor: "pointer",
      itemclick: (e) => toggleDataSeries(e),
    },
    data:
      dashboardType === "column"
        ? optionsDashboardDataColumn
        : optionsDashboardDataLine,
  };

  useEffect(() => {
    Promise.all([getPickets(), getLots()]);
  }, [getLots, getPickets]);

  useEffect(() => {
    generateChart();
  }, [generateChart, dashboardData]);

  return (
    <DashboardContainer>
      <Spin spinning={loading}>
        <Formik
          initialValues={INITIAL_PARAMETERS_VALUES}
          onSubmit={handleFilterDashboard}
          render={({
            values,
            errors,
            setFieldValue,
            submitCount,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              <Row
                type="flex"
                gutter={8}
                align="middle"
                style={{ marginBottom: "16px", marginTop: "16px" }}
              >
                <Col xs={12} sm={12} md={8} lg={4} xl={4} xxl={4}>
                  <Row>
                    <label>
                      {translation.picketSupplementSupply.dashboard.picketId}
                    </label>
                  </Row>
                  <Row className="rowInput">
                    <Select
                      name="picketId"
                      value={values.picketIds || undefined}
                      placeholder="Selecione o piquete"
                      mode="multiple"
                      allowClear
                      onChange={(value) => {
                        setFieldValue("picketIds", value);
                      }}
                    >
                      {pickets
                        .filter(
                          (picket) =>
                            picket.status !== "Inactive" &&
                            picket.status !== "Maintenance" &&
                            picket.status !== "Renovation",
                        )
                        .map((picket) => (
                          <Select.Option key={picket.id} value={picket.id}>
                            {picket.name}
                          </Select.Option>
                        ))}
                    </Select>
                  </Row>
                </Col>
                <Col xs={12} sm={12} md={8} lg={4} xl={4} xxl={4}>
                  <Row>
                    <label>
                      {translation.picketSupplementSupply.dashboard.lotId}
                    </label>
                  </Row>
                  <Row className="rowInput">
                    <Select
                      value={values.lotIds || undefined}
                      placeholder={translation.defaultSelectPlaceholder}
                      mode="multiple"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      allowClear
                      onChange={(value) => setFieldValue("lotIds", value)}
                    >
                      {lots.map((lot) => (
                        <Select.Option key={lot.id} value={lot.id}>
                          {lot.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Row>
                </Col>
                <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
                  <Row>
                    <label className="filter-label">
                      {translation.picketSupplementSupply.dashboard.periodType}
                    </label>
                  </Row>
                  <Row>
                    <Select
                      style={{ width: "100%" }}
                      name="periodType"
                      value={values.periodType || undefined}
                      placeholder={translation.defaultSelectPlaceholder}
                      onChange={(periodType) => {
                        setFieldValue("periodType", periodType);
                        if (periodType !== "customDateRange") {
                          setFieldValue("startDate", null);
                          setFieldValue("endDate", null);
                        }
                      }}
                    >
                      {LIST_PERIODS?.map((period) => (
                        <Select.Option key={period} value={period}>
                          {
                            translation.financialCashFlowReportModalRequest
                              .periods[period]
                          }
                        </Select.Option>
                      ))}
                    </Select>
                  </Row>
                  {values.periodType === "customDateRange" && (
                    <Row type="flex" gutter={8} style={{ marginTop: "8px" }}>
                      <Col span={12}>
                        <Row>
                          <label>
                            {
                              translation.picketSupplementSupply.dashboard
                                .startDate
                            }
                          </label>
                        </Row>
                        <Row>
                          <DatePicker
                            name="startDate"
                            value={values.startDate || null}
                            placeholder={
                              translation.defaultDatePickerPlaceholder
                            }
                            format={"DD/MM/YYYY"}
                            onChange={(date) => {
                              setFieldValue(
                                "startDate",
                                momentTimeZoneZeroHour(date),
                              );
                              if (
                                values.endDate &&
                                date.isAfter(values.endDate)
                              )
                                setFieldValue("endDate", null);
                            }}
                          />
                        </Row>
                      </Col>
                      <Col span={12}>
                        <Row>
                          <label>
                            {
                              translation.picketSupplementSupply.dashboard
                                .endDate
                            }
                          </label>
                        </Row>
                        <Row>
                          <DatePicker
                            name="endDate"
                            value={values.endDate || null}
                            placeholder={
                              translation.defaultDatePickerPlaceholder
                            }
                            format={"DD/MM/YYYY"}
                            disabledDate={(current) =>
                              current.isAfter(momentTimeZoneZeroHour())
                            }
                            onChange={(date) =>
                              setFieldValue(
                                "endDate",
                                momentTimeZoneZeroHour(date),
                              )
                            }
                          />
                        </Row>
                      </Col>
                    </Row>
                  )}
                </Col>
                <Col xs={24} sm={24} md={8} lg={4} xl={4} xxl={4}>
                  <Row>
                    <label>
                      {
                        translation.picketSupplementSupply.dashboard
                          .exibitionType
                      }
                    </label>
                  </Row>
                  <Row className="rowInput">
                    <Select
                      value={dashboardType}
                      placeholder={translation.defaultSelectPlaceholder}
                      onChange={(value) => {
                        setDashboardType(value);
                        if (chartRef && chartRef.current) {
                          chartRef.current.render();
                        }
                      }}
                    >
                      <Select.Option value="column">
                        {
                          translation.picketSupplementSupply.dashboard
                            .exibitionTypeOptions.column
                        }
                      </Select.Option>
                      <Select.Option value="line">
                        {
                          translation.picketSupplementSupply.dashboard
                            .exibitionTypeOptions.line
                        }
                      </Select.Option>
                    </Select>
                  </Row>
                </Col>
                <Col xs={24} sm={24} md={24} lg={4} xl={4} xxl={4}>
                  <Row type="flex" justify="center" align="middle">
                    <ButtonStandard
                      name="buttonFilter"
                      buttonType="addNew"
                      onClick={handleSubmit}
                    >
                      {
                        translation.picketSupplementSupply.dashboard
                          .buttonProcessDashboard
                      }
                    </ButtonStandard>
                  </Row>
                </Col>
              </Row>
            </form>
          )}
        />
        <Row type="flex">
          <Col span={24}>
            <div className="dashboard-container">
              {dashboardData.length === 0 && (
                <Empty
                  description={`Nenhum dado do dashboard encontrado. Clique em "${
                    translation.picketSupplementSupply.dashboard
                      .buttonProcessDashboard
                  }" para gerar os dados.`}
                />
              )}
              {dashboardData.length > 0 && (
                <CanvasJSChart
                  options={optionsDashboard}
                  onRef={(ref) => (chartRef = ref)}
                />
              )}
            </div>
          </Col>
        </Row>
      </Spin>
    </DashboardContainer>
  );
}

export default PicketSupplementSupplyTableDashboard;
